import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AppState } from 'src/app/store';
import { Feature } from 'src/app/features/feature-management/enums/feature.enum';
import { FeatureManagementService } from 'src/app/features/feature-management/services/feature-management.service';
import { DeviceSupportService } from 'src/app/features/device-support/services/device-support.service';
import {
  ClientHints,
  DeviceSupportResult,
} from 'src/app/features/device-support/models/client-hints.model';
import {
  selectParticipantId,
  selectSigningId,
} from 'src/app/features/session/store/selectors/session.selectors';

const UNSUPPORTED_BROWSER_ROUTE = '/unsupported-browser';
const UNSUPPORTED_DEVICE_ROUTE = '/unsupported-device';
const UNSUPPORTED_OS_ROUTE = '/unsupported-operating-system';

export const deviceSupportGuard: CanActivateFn = (route) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const router = inject(Router);
  const featureService = inject(FeatureManagementService);
  const deviceSupport = inject(DeviceSupportService);
  const store = inject(Store<AppState>);
  const signingId = store.selectSignal(selectSigningId)();
  const participantId =
    store.selectSignal(selectParticipantId)() ||
    (route.queryParams['participantId'] as string | undefined) ||
    undefined;

  return featureService.getFeatureFlags().pipe(
    catchError(() => of([])),
    switchMap((flags) => {
      const blockingEnabled = flags.some(
        (f) => f.featureFlag === Feature.DeviceBrowserBlocking && f.isEnabled,
      );

      if (!blockingEnabled) {
        return of(true as boolean | UrlTree);
      }

      return runCheck(deviceSupport).pipe(
        switchMap(({ result, clientHints }) =>
          deviceSupport
            .logResult({
              isBrowserSupported: result.isBrowserSupported,
              isDeviceSupported: result.isDeviceSupported,
              isOperatingSystemSupported: result.isOperatingSystemSupported,
              clientHints: clientHints ?? undefined,
              signingId: signingId || undefined,
              participantId,
            })
            .pipe(map(() => resolveRedirect(result, router, route.queryParams))),
        ),
      );
    }),
  );
};

interface CheckResult {
  result: DeviceSupportResult;
  clientHints: ClientHints | null;
}

function runCheck(deviceSupport: DeviceSupportService): Observable<CheckResult> {
  return deviceSupport.getClientHints().pipe(
    switchMap((clientHints: ClientHints | null) => {
      if (clientHints) {
        return deviceSupport
          .checkByClientHints(clientHints)
          .pipe(map((result) => ({ result, clientHints })));
      }
      return deviceSupport
        .checkByUserAgent()
        .pipe(map((result) => ({ result, clientHints: null })));
    }),
  );
}

function resolveRedirect(
  result: DeviceSupportResult,
  router: Router,
  queryParams: Record<string, unknown>,
): boolean | UrlTree {
  if (!result.isDeviceSupported) {
    return router.createUrlTree([UNSUPPORTED_DEVICE_ROUTE], { queryParams });
  }
  if (!result.isOperatingSystemSupported) {
    return router.createUrlTree([UNSUPPORTED_OS_ROUTE], { queryParams });
  }
  if (!result.isBrowserSupported) {
    return router.createUrlTree([UNSUPPORTED_BROWSER_ROUTE], { queryParams });
  }
  return true;
}
